import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { AxiosError } from 'axios';
import { ingestApi } from '../api/ingest';
import { seriesApi } from '../api/series';
import { teamsApi } from '../api/teams';
import type { ParsedFile } from '../utils/parseChapterNumber';
import Card from '../components/Card';
import Button from '../components/Button';
import ChapterDropZone from '../components/ChapterDropZone';

interface UploadResult {
  filename: string;
  chapterNo: number | null;
  jobId?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export default function UploadPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [seriesId, setSeriesId] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [files, setFiles] = useState<ParsedFile[]>([]);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch teams and series
  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.getAll(),
  });

  const { data: series } = useQuery({
    queryKey: ['series', teamFilter],
    queryFn: () => seriesApi.getAll(teamFilter || undefined),
  });

  // Prefill from query params
  useEffect(() => {
    const sid = searchParams.get('series_id');
    if (sid) setSeriesId(sid);
  }, [searchParams]);

  // Handle series ID change and set team filter if series belongs to a team
  useEffect(() => {
    if (seriesId && series) {
      const selectedSeries = series.find(s => s.id === seriesId);
      const firstTeamId = selectedSeries?.teams?.[0]?.id;
      if (firstTeamId && firstTeamId !== teamFilter) {
        setTeamFilter(firstTeamId);
      }
    }
  }, [seriesId, series, teamFilter]);

  const handleFilesSelected = (parsedFiles: ParsedFile[]) => {
    setFiles(parsedFiles);
    setUploadResults([]);
  };

  const allValid = files.length > 0 && files.every(f => f.isValid);
  const hasFiles = files.length > 0;

  const handleUpload = async () => {
    if (!hasFiles || !seriesId) return;

    setIsUploading(true);

    // Initialize results
    const results: UploadResult[] = files.map(f => ({
      filename: f.filename,
      chapterNo: f.chapterNo,
      status: 'pending' as const,
    }));
    setUploadResults(results);

    // Upload files in parallel (max 3 at a time)
    const concurrency = 3;
    let currentIndex = 0;

    const uploadNext = async (): Promise<void> => {
      const index = currentIndex++;
      if (index >= files.length) return;

      const file = files[index];

      // Update status to uploading
      setUploadResults(prev => {
        const newResults = [...prev];
        newResults[index] = { ...newResults[index], status: 'uploading' };
        return newResults;
      });

      try {
        const response = await ingestApi.upload(
          file.file,
          seriesId,
          file.chapterNo ?? undefined,
          undefined
        );

        setUploadResults(prev => {
          const newResults = [...prev];
          newResults[index] = {
            ...newResults[index],
            status: 'success',
            jobId: response.job_id,
          };
          return newResults;
        });
      } catch (error) {
        const axiosError = error as AxiosError<{ error?: string; message?: string }>;
        const errorMessage =
          axiosError?.response?.status === 413
            ? 'Файл слишком большой'
            : axiosError?.response?.data?.error ||
              axiosError?.response?.data?.message ||
              'Ошибка загрузки';

        setUploadResults(prev => {
          const newResults = [...prev];
          newResults[index] = {
            ...newResults[index],
            status: 'error',
            error: errorMessage,
          };
          return newResults;
        });
      }

      // Continue with next file
      await uploadNext();
    };

    // Start parallel uploads
    const promises = [];
    for (let i = 0; i < Math.min(concurrency, files.length); i++) {
      promises.push(uploadNext());
    }
    await Promise.all(promises);

    setIsUploading(false);

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries({ queryKey: ['chapters', seriesId] });
  };

  const successCount = uploadResults.filter(r => r.status === 'success').length;
  const errorCount = uploadResults.filter(r => r.status === 'error').length;
  const allDone = uploadResults.length > 0 && uploadResults.every(r => r.status === 'success' || r.status === 'error');

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Загрузка глав
      </h1>

      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Team filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Команда (фильтр)
            </label>
            <select
              value={teamFilter}
              onChange={(e) => {
                setTeamFilter(e.target.value);
                setSeriesId(''); // Reset series when team changes
              }}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
            >
              <option value="">Все команды</option>
              {teams?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Series selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Серия <span className="text-red-500">*</span>
            </label>
            <select
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
              required
            >
              <option value="">Выберите серию</option>
              {series?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                  {s.teams && s.teams.length > 0 && ` (${s.teams.map(t => t.name).join(', ')})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Drop zone */}
        <ChapterDropZone
          onFilesSelected={handleFilesSelected}
          disabled={isUploading}
        />

        {/* Validation message */}
        {hasFiles && !allValid && !isUploading && (
          <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="ml-3 text-sm text-amber-800 dark:text-amber-300">
              У некоторых файлов не определен номер главы. Укажите номера вручную перед загрузкой.
            </p>
          </div>
        )}

        {/* Upload button */}
        {hasFiles && (
          <div className="mt-6">
            <Button
              onClick={handleUpload}
              disabled={!seriesId || !allValid || isUploading}
              className="w-full"
            >
              {isUploading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Загрузка... ({uploadResults.filter(r => r.status === 'success' || r.status === 'error').length}/{files.length})
                </span>
              ) : (
                <>
                  <UploadIcon className="h-4 w-4 mr-2" />
                  Загрузить {files.length} {files.length === 1 ? 'главу' : files.length < 5 ? 'главы' : 'глав'}
                </>
              )}
            </Button>
          </div>
        )}
      </Card>

      {/* Upload results */}
      {uploadResults.length > 0 && (
        <Card title="Результаты загрузки">
          {allDone && (
            <div className={`mb-4 p-4 rounded-lg ${
              errorCount === 0
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
            }`}>
              <div className="flex items-center">
                {errorCount === 0 ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                    <span className="text-green-800 dark:text-green-300">
                      Все {successCount} глав успешно загружены!
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2" />
                    <span className="text-amber-800 dark:text-amber-300">
                      Загружено {successCount} глав, {errorCount} с ошибками
                    </span>
                  </>
                )}
              </div>
              {allDone && (
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/series/${seriesId}`)}
                  >
                    Перейти к серии
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            {uploadResults.map((result, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  result.status === 'success'
                    ? 'bg-green-50 dark:bg-green-900/10'
                    : result.status === 'error'
                    ? 'bg-red-50 dark:bg-red-900/10'
                    : result.status === 'uploading'
                    ? 'bg-blue-50 dark:bg-blue-900/10'
                    : 'bg-gray-50 dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {result.status === 'uploading' && (
                    <Loader2 className="h-5 w-5 text-blue-500 animate-spin flex-shrink-0" />
                  )}
                  {result.status === 'success' && (
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  )}
                  {result.status === 'error' && (
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  )}
                  {result.status === 'pending' && (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}

                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      Глава {result.chapterNo ?? '?'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {result.filename}
                    </p>
                  </div>
                </div>

                <div className="text-sm text-right ml-4">
                  {result.status === 'success' && result.jobId && (
                    <span className="text-green-600 dark:text-green-400">
                      Job: {result.jobId.slice(0, 8)}...
                    </span>
                  )}
                  {result.status === 'error' && (
                    <span className="text-red-600 dark:text-red-400">
                      {result.error}
                    </span>
                  )}
                  {result.status === 'uploading' && (
                    <span className="text-blue-600 dark:text-blue-400">
                      Загрузка...
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
